import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import {
  alpha,
  Box,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  Fade,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Imobiliario, ImobiliarioEdificacao } from "../../../interfaces";
import { getVinculosImobiliarioEdificacaoPorLote } from "../../../services/api";
import { getAll } from "../../../services/db";
import {
  AcaoVinculoUnificacaoRioBranco,
  buildPayloadVinculosUnificacao,
  criarConfigPadraoVinculoUnificacao,
  getLabelTipoVinculo,
  getVinculosAtivosParaUnificacao,
  VinculoUnificacaoConfig,
  VinculoUnificacaoPayloadItem,
} from "../../../utils/unificarImobiliarioRioBranco";
import { getSubunidadesAtivas, imobiliarioTemPrincipalAtiva } from "../../../utils/vincularEdificacaoRioBranco";
import {
  IconComplementarSubunidade,
  OpcaoVinculoUi,
  vinculoRioBrancoDialogPaperProps,
  VinculoRioBrancoDialogActions,
  VinculoRioBrancoDialogChips,
  VinculoRioBrancoDialogHeader,
  VinculoRioBrancoDialogLoading,
  VinculoRioBrancoOpcoesList,
} from "../VinculoRioBrancoDialogUi";

interface Props {
  openDialog: boolean;
  idImobiliarioCorporadora: string;
  imovelUnificado: Imobiliario;
  idLote?: string;
  onClose: () => void;
  onConfirm: (payload: VinculoUnificacaoPayloadItem[]) => void;
  processing?: boolean;
}

export function DialogUnificarVinculosRioBranco({
  openDialog,
  idImobiliarioCorporadora,
  imovelUnificado,
  idLote,
  onClose,
  onConfirm,
  processing = false,
}: Props) {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [vinculos, setVinculos] = useState<ImobiliarioEdificacao[]>([]);
  const [vinculosCorporadora, setVinculosCorporadora] = useState<ImobiliarioEdificacao[]>([]);
  const [configs, setConfigs] = useState<Record<string, VinculoUnificacaoConfig>>({});

  const subunidadesCorporadora = useMemo(
    () => getSubunidadesAtivas(vinculosCorporadora, idImobiliarioCorporadora),
    [vinculosCorporadora, idImobiliarioCorporadora],
  );

  const corporadoraTemPrincipal = useMemo(
    () => imobiliarioTemPrincipalAtiva(vinculosCorporadora, idImobiliarioCorporadora),
    [vinculosCorporadora, idImobiliarioCorporadora],
  );

  const opcoes = useMemo<OpcaoVinculoUi[]>(() => {
    const lista: OpcaoVinculoUi[] = [];

    if (!corporadoraTemPrincipal) {
      lista.push({
        value: "principal",
        titulo: "Principal",
        descricao: "Define como edificação principal do imobiliário que permanece.",
        icon: <HomeOutlinedIcon sx={{ fontSize: 20 }} />,
        accent: theme.palette.primary.main,
      });
    }

    lista.push({
      value: "subunidade_imovel",
      titulo: "Subunidade",
      descricao: "Vincula como subunidade da edificação principal do imobiliário.",
      icon: <ApartmentOutlinedIcon sx={{ fontSize: 20 }} />,
      accent: theme.palette.info.main,
    });

    lista.push({
      value: "complementar_principal",
      titulo: "Complementar da principal",
      descricao: "Vincula como complementar da edificação principal.",
      icon: <LinkOutlinedIcon sx={{ fontSize: 20 }} />,
      accent: theme.palette.warning.main,
    });

    if (subunidadesCorporadora.length > 0) {
      lista.push({
        value: "complementar_subunidade",
        titulo: "Complementar da subunidade",
        descricao: "Vincula como complementar de uma subunidade existente.",
        icon: <IconComplementarSubunidade />,
        accent: theme.palette.warning.main,
      });
    }

    return lista;
  }, [corporadoraTemPrincipal, subunidadesCorporadora.length, theme.palette]);

  useEffect(() => {
    if (!openDialog) return;

    const carregar = async () => {
      setLoading(true);
      try {
        const locais = (getAll("imobiliario_edificacao") || []) as ImobiliarioEdificacao[];
        let merged = [...locais];

        if (idLote) {
          const vinculosApi = await getVinculosImobiliarioEdificacaoPorLote(Number(idLote));
          const mapa = new Map<string, ImobiliarioEdificacao>();
          [...locais, ...vinculosApi].forEach((v) => {
            if (v?.id != null) mapa.set(String(v.id), v as ImobiliarioEdificacao);
          });
          merged = Array.from(mapa.values());
        }

        const vinculosUnificar = getVinculosAtivosParaUnificacao(merged, imovelUnificado.id);
        const vinculosCorp = merged.filter(
          (v) => String(v.id_imobiliario) === String(idImobiliarioCorporadora),
        );

        setVinculos(vinculosUnificar);
        setVinculosCorporadora(vinculosCorp);

        const iniciais: Record<string, VinculoUnificacaoConfig> = {};
        vinculosUnificar.forEach((vinculo) => {
          iniciais[String(vinculo.id)] = criarConfigPadraoVinculoUnificacao(
            merged,
            idImobiliarioCorporadora,
            vinculo,
          );
        });
        setConfigs(iniciais);
      } finally {
        setLoading(false);
      }
    };

    carregar();
  }, [openDialog, imovelUnificado.id, idImobiliarioCorporadora, idLote]);

  function atualizarConfig(idVinculo: string, patch: Partial<VinculoUnificacaoConfig>) {
    setConfigs((atual) => ({
      ...atual,
      [idVinculo]: { ...atual[idVinculo], ...patch },
    }));
  }

  function selecionarAcao(idVinculo: string, acao: string) {
    const patch: VinculoUnificacaoConfig = { acao: acao as AcaoVinculoUnificacaoRioBranco };
    if (acao === "complementar_subunidade" && subunidadesCorporadora[0]) {
      patch.id_edificacao_alvo = String(subunidadesCorporadora[0].id_edificacao);
    }
    atualizarConfig(idVinculo, patch);
  }

  function handleConfirmar() {
    const invalido = vinculos.some((vinculo) => {
      const config = configs[String(vinculo.id)];
      return config?.acao === "complementar_subunidade" && !config.id_edificacao_alvo;
    });

    if (invalido) {
      toast.error("Selecione a subunidade de referência.");
      return;
    }

    onConfirm(buildPayloadVinculosUnificacao(vinculos, configs));
  }

  return (
    <Dialog
      open={openDialog}
      onClose={processing ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={vinculoRioBrancoDialogPaperProps}
    >
      <VinculoRioBrancoDialogHeader
        icon={<LinkOutlinedIcon sx={{ fontSize: 22 }} />}
        title="Tipo de vínculo"
        subtitle="Escolha como cada edificação do imóvel unificado se relaciona com o imóvel que permanece."
        onClose={onClose}
        disabled={processing}
      />

      <VinculoRioBrancoDialogChips
        labels={[
          `Imobiliário unificado #${imovelUnificado.id}`,
          `Destino #${idImobiliarioCorporadora}`,
        ]}
      />

      <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
        {loading ? (
          <VinculoRioBrancoDialogLoading message="Verificando vínculos..." />
        ) : (
          <Fade in={!loading}>
            <Box
              sx={{
                maxHeight: vinculos.length > 1 ? "min(50vh, 480px)" : undefined,
                overflowY: vinculos.length > 1 ? "auto" : undefined,
                pr: vinculos.length > 1 ? 0.5 : 0,
              }}
            >
              {vinculos.map((vinculo, index) => {
                const idVinculo = String(vinculo.id);
                const config = configs[idVinculo] ?? { acao: "complementar_principal" as const };

                return (
                  <Box key={idVinculo}>
                    {vinculos.length > 1 && (
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                        <Chip
                          size="small"
                          label={`Edificação #${vinculo.id_edificacao}`}
                          variant="outlined"
                          sx={{ borderRadius: 1.5, fontWeight: 500 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {getLabelTipoVinculo(vinculo.tipo_vinculo)} atual
                        </Typography>
                      </Stack>
                    )}

                    <VinculoRioBrancoOpcoesList
                      opcoes={opcoes}
                      acaoSelecionada={config.acao}
                      onSelectAcao={(acao) => selecionarAcao(idVinculo, acao)}
                      subunidades={subunidadesCorporadora}
                      subunidadeAlvoId={
                        config.id_edificacao_alvo ??
                        (subunidadesCorporadora[0]
                          ? String(subunidadesCorporadora[0].id_edificacao)
                          : "")
                      }
                      onSubunidadeAlvoChange={(id) =>
                        atualizarConfig(idVinculo, { id_edificacao_alvo: id })
                      }
                      subunidadeValueKey={(sub) => String(sub.id_edificacao)}
                      subunidadeLabelId={`subunidade-referencia-unificar-${idVinculo}`}
                      ariaLabel={`Tipo de vínculo edificação ${vinculo.id_edificacao}`}
                    />

                    {index < vinculos.length - 1 && (
                      <Divider sx={{ my: 2.5, borderColor: alpha(theme.palette.divider, 0.8) }} />
                    )}
                  </Box>
                );
              })}
            </Box>
          </Fade>
        )}
      </DialogContent>

      <VinculoRioBrancoDialogActions
        onCancel={onClose}
        onConfirm={handleConfirmar}
        confirmLabel="Confirmar unificação"
        processingLabel="Unificando..."
        loading={loading}
        processing={processing}
        confirmDisabled={vinculos.length === 0}
      />
    </Dialog>
  );
}
